import React, { useEffect, useState } from "react";
import { Container, Row, Col, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import styles from "../../../Library/AdverseEvent/css/adverseEvent.module.scss";
import { LINE_CLEARANCE_PATHS } from "../../../../Routes/paths";
import {
  GetUserData,
  perPageData,
  showToastMessage,
} from "../../../../common/service/CommonService";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import { useLineClearanceService } from "../api/useLineClearance";
import { queryClient } from "../../../../api/queryClient";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import CommonButton from "../../../../Components/Common/CommonButton";
import Reason, { ReasonSubmitPayload } from "common/utilities/Reason/Reason";
import PinVerificationModal from "../../DailyReconsilation/components/PinVerificationModal";
import StatCard from "../../../../Components/Common/StatCard";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useGlobalStudy } from "../../../../context/StudyContext";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import NoStudySelectedPlaceholder from "../../../../Components/Common/NoStudySelectedPlaceholder";
import { getLineClearanceColumns } from "../helpers/lineClearance.constant";
import { usePermission } from "context/PermissionContext";

const LineClearanceList: React.FC = () => {
  document.title = "Line Clearance | ZENTIXS ORBIT";

  /* -----------------------------------------------------------------
   * 1. State & Router Hooks
   * ----------------------------------------------------------------- */
  const navigate = useNavigate();
  const userData = GetUserData();
  const { selectedStudy: globalStudy } = useGlobalStudy();

  // Search & Pagination State
  const [searchText, setSearchText] = useState<string>("");
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(null);
  // const [totalRecords, setTotalRecords] = useState<number>(0);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedDeleteRecord, setSelectedDeleteRecord] = useState<any>(null);

  // Verification Modal State
  const [verifyModal, setVerifyModal] = useState<boolean>(false);
  const [selectedVerifyRecord, setSelectedVerifyRecord] = useState<any>(null);

  // Audit Log State
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  /* -----------------------------------------------------------------
   * 2. API Queries & Mutations
   * ----------------------------------------------------------------- */
  const { SelectAllMutation: AuditMutation } = useAuditService();

  // Fetch Study details for periods list
  const { SelectQuery: studySelectQuery } = useMasterStudyService({
    id: Number(globalStudy?.studyid) || null,
    enabledSelect: !!globalStudy?.studyid,
  });

  // Fetch Line Clearance Records
  const { SelectAllQuery, DeleteMutation, verifyPinMutation } = useLineClearanceService({
    searchtext: searchText,
    currentpage: currentPageNumber,
    pagesize: 5,
    studyid: Number(globalStudy?.studyid) || null,
    periodid: selectedPeriodId,
  });

  /* -----------------------------------------------------------------
   * 3. Derived Data & Effects
   * ----------------------------------------------------------------- */
  // Extract Study Periods
  const rawStudyResponse: any = studySelectQuery.data;
  let studyData = rawStudyResponse?.data;
  if (Array.isArray(studyData)) {
    studyData = studyData[0];
  } else if (studyData?.data) {
    studyData = Array.isArray(studyData.data) ? studyData.data[0] : studyData.data;
  }
  const periodsList: any[] = studyData?.periods || [];

  // Extract Line Clearance Table Data
  const { data: queryResponse, isLoading, isFetching } = SelectAllQuery as {
    data?: { data?: any[] };
    isLoading: boolean;
    isFetching: boolean;
  };
  const tableData: any[] = queryResponse?.data ?? [];

  // Auto-select first period when periods list loads
  useEffect(() => {
    if (periodsList.length > 0) {
      const exists = periodsList.some(
        (p: any) => Number(p.period_id) === Number(selectedPeriodId)
      );
      if (!exists) {
        setSelectedPeriodId(periodsList[0].period_id);
      }
    } else {
      setSelectedPeriodId(null);
    }
  }, [periodsList, selectedPeriodId]);

  /* -----------------------------------------------------------------
   * 4. Event Handlers
   * ----------------------------------------------------------------- */
  // Search Handler
  const handleSearch = (value: string | null) => {
    setSearchText(value ?? "");
    setCurrentPageNumber(1);
  };

  // View Record Handler
  const handleViewClick = (row: any) => {
    navigate(LINE_CLEARANCE_PATHS.VIEW.replace(":id", row.lineclearanceid.toString()));
  };

  // Audit Trail Handler
  const handleAuditClick = () => {
    const payload = {
      modulename: "Line Clearance",
      studyid: globalStudy?.studyid,
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Line_Clearance_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  // Delete Record Handlers
  const handleDeleteConfirm = (payload: ReasonSubmitPayload) => {
    if (!selectedDeleteRecord) return;

    const deletePayload = {
      lineclearanceid: Number(selectedDeleteRecord.lineclearanceid),
      updatereason: payload.reason,
    };

    DeleteMutation.mutate(deletePayload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message || "Line Clearance record deleted successfully.");
        queryClient.invalidateQueries({ queryKey: ["line-clearance-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        setIsDeleteModalOpen(false);
        setSelectedDeleteRecord(null);
      },
      onError: (err: any) => {
        showToastMessage("error", err || "Failed to delete Line Clearance record.");
      },
    });
  };

  // PIN Verification Handlers
  const handleVerifyClick = (item: any) => {
    setSelectedVerifyRecord(item);
    setVerifyModal(true);
  };

  const handlePinVerify = (password: string) => {
    const payload = {
      verifypin: password,
      lineclearanceid: Number(selectedVerifyRecord?.lineclearanceid),
      studyid: Number(selectedVerifyRecord?.studyid || globalStudy?.studyid),
      verifiedby: Number(userData?.userid),
    };

    verifyPinMutation.mutate(payload, {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries({ queryKey: ["line-clearance-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        showToastMessage("success", response?.message || "Line Clearance verified successfully.");
        setVerifyModal(false);
        setSelectedVerifyRecord(null);
      },
      onError: (error: Error) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  // Dynamic Row Action Buttons
  const getRowActions = (row: any): any => {
    const actions: any = {};

    if (!row.isverified) {
      actions.onEdit = (r: any) => {
        navigate(LINE_CLEARANCE_PATHS.EDIT.replace(":id", r.lineclearanceid.toString()));
      };
      actions.onDelete = (r: any) => {
        setSelectedDeleteRecord(r);
        setIsDeleteModalOpen(true);
      };
      actions.onVerify = (r: any) => {
        handleVerifyClick(r);
      };
    }

    return actions;
  };

  const {hasPermission}= usePermission()

  /* -----------------------------------------------------------------
   * 5. Main Component Render
   * ----------------------------------------------------------------- */
  return (
    <div className={styles.dashboardContainer}>
      <Container fluid>
        {!globalStudy?.studyid ? (
          <div>
            <NoStudySelectedPlaceholder moduleName="inventory" />
          </div>
        ) : (
          <>
            {/* Header Title & Action Buttons */}
            <div className="d-flex justify-content-between align-items-center mt-4 mb-3 px-2">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h5 className={`mb-0 ${styles.pageHeaderTitle}`}>Line Clearance</h5>
                </div>
                <p className={styles.pageHeaderSubtitle}>
                  List view of all line clearances of investigational products before drug dispensation.
                </p>
              </div>

              <div className="d-flex gap-3 align-items-center">
                <CommonButton
                  module={PERMISSION_MODULES.LINE_CLEARANCE}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={handleAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                  className="fw-600"
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1" />
                  )}
                  Audit
                </CommonButton>

                <CommonButton
                  module={PERMISSION_MODULES.LINE_CLEARANCE}
                  action={PERMISSION_ACTIONS.ADD}
                  color="primary"
                  outline
                  className="px-2 fw-600"
                  size="sm"
                  onClick={() => navigate(LINE_CLEARANCE_PATHS.ADD)}
                >
                  <i className="ri-add-line me-1" /> Add Line Clearance
                </CommonButton>
              </div>
            </div>

            {/* Stats Summary Cards */}
            <Row className="g-3 mb-3 px-1">
              <Col md={4}>
                <StatCard
                  title="TOTAL RECORDS"
                  value={tableData[0]?.total_count || 0}
                  subtext="Line clearances created"
                  icon={<i className="ri-database-2-line" />}
                  bgColor="#eff6ff"
                  iconColor="#2563eb"
                  trendType="info"
                  subtextIcon="ri-stack-line"
                  loading={SelectAllQuery.isFetching}
                />
              </Col>

              <Col md={4}>
                <StatCard
                  title="PENDING REVIEW"
                  value={tableData[0]?.pending_review_count || 0}
                  subtext="Needs QA review"
                  icon={<i className="ri-search-eye-line" />}
                  bgColor="#fffbeb"
                  iconColor="#d97706"
                  trendType="negative"
                  subtextIcon="ri-error-warning-line"
                  loading={SelectAllQuery.isFetching}
                />
              </Col>

              <Col md={4}>
                <StatCard
                  title="PENDING VERIFICATION"
                  value={tableData[0]?.pending_verification_count || 0}
                  subtext="Pending Verification"
                  icon={<i className="ri-time-line" />}
                  bgColor="#fffbeb"
                  iconColor="#d97706"
                  trendType="success"
                  subtextIcon="ri-check-double-line"
                  loading={SelectAllQuery.isFetching}
                />
              </Col>
            </Row>

            {/* List Table Panel */}
            <div className={styles.listPanel}>
              {/* Panel Header: Title, Period Tabs & Search */}
              <div className={`${styles.panelHeader} d-flex align-items-center justify-content-between`}>
                <div className="d-flex align-items-center gap-3">
                  <h2 className="mb-0">
                    <i className="ri-medicine-bottle-line me-2 text-primary fs-18" />
                    Line Clearance Records
                  </h2>
                </div>

                {/* Period Filter Tabs */}
                {periodsList.length > 0 && (
                  <div
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: "#f3f6f9",
                      borderRadius: "50px",
                      padding: "4px",
                      border: "1px solid #e9ebec",
                    }}
                  >
                    {periodsList.map((period: any) => {
                      const isActive = Number(selectedPeriodId) === Number(period.period_id);
                      const rawNumber = period.period_number || period.period_no || period.period_id;
                      const formattedPeriod = String(rawNumber).padStart(2, "0");

                      return (
                        <button
                          key={period.period_id}
                          type="button"
                          className={`btn btn-sm px-4 rounded-pill fw-semibold border-0 ${
                            isActive ? "text-primary shadow-sm" : "text-muted"
                          }`}
                          style={{
                            backgroundColor: isActive ? "#ffffff" : "transparent",
                            transition: "all 0.2s ease-in-out",
                          }}
                          onClick={() => {
                            setSelectedPeriodId(period.period_id);
                            setCurrentPageNumber(1);
                          }}
                        >
                          <i className="ri-stack-line me-1" />
                          Period {formattedPeriod}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Search Bar */}
                <div className="d-flex align-items-center">
                  <CommonSearch onSearch={handleSearch} searchText={searchText} />
                </div>
              </div>

              {/* Data Table */}
              <div className="px-4 pb-2">
                <CommonTableIndexWise
                  moduleName={PERMISSION_MODULES.LINE_CLEARANCE}
                  columns={getLineClearanceColumns(handleViewClick,hasPermission)}
                  data={tableData}
                  loading={isLoading || isFetching}
                  pageSize={5}
                  currentPage={currentPageNumber}
                  totalRecords={tableData[0]?.total_count || 0}
                  onPageChange={setCurrentPageNumber}
                  actions={getRowActions}
                />
              </div>

              {/* PDF / Audit Preview Modal */}
              {isPreviewOpen && (
                <FilePreviewModal
                  file={previewFile}
                  isOpen={isPreviewOpen}
                  size="xl"
                  onClose={() => {
                    setIsPreviewOpen(false);
                    setPreviewFile(null);
                  }}
                />
              )}

              {/* Reason Modal (Delete) */}
              {isDeleteModalOpen && (
                <Reason
                  isOpen={isDeleteModalOpen}
                  toggle={() => {
                    setIsDeleteModalOpen(false);
                    setSelectedDeleteRecord(null);
                  }}
                  action="Deactive Line Clearance Record"
                  id={selectedDeleteRecord?.lineclearanceid}
                  click={handleDeleteConfirm}
                  isLoading={DeleteMutation.isPending}
                />
              )}

              {/* PIN Verification Modal */}
              <PinVerificationModal
                isOpen={verifyModal}
                toggle={() => setVerifyModal(false)}
                onVerify={handlePinVerify}
                isLoading={verifyPinMutation.isPending}
                title="Verify Line Clearance Record"
                description={
                  <>
                    Please enter your password to verify Line Clearance Record ID:{" "}
                    <strong>{`${selectedVerifyRecord?.lineclearanceid || ""}`}</strong>
                  </>
                }
                isInvalid={verifyPinMutation.isError}
              />
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default LineClearanceList;
